const preorderLayoutStates = new WeakMap();

function getButtonLabel(button) {
  return button?.dataset.preorderFallbackOriginalLabel || button?.textContent || '';
}

function isPreorderButton(button) {
  return button?.dataset.themePreorderLabelActive === 'true'
    || /pre[\s-]?order/i.test(String(getButtonLabel(button)).trim());
}

function isSoldOutButton(button) {
  return Boolean(button?.disabled) && /sold out|out of stock|unavailable/i.test(String(getButtonLabel(button)).trim());
}

function hasStoqPreorderPlan(buttonsContainer) {
  const variantId = buttonsContainer.closest('product-info')?.querySelector('.product-variant-id')?.value;
  const getSellingPlan = window._RestockRocket?.getSellingPlan;
  const stoqVariantId = Number(variantId);
  if (typeof getSellingPlan !== 'function' || !Number.isSafeInteger(stoqVariantId)) return false;

  try {
    return Boolean(getSellingPlan(stoqVariantId));
  } catch (error) {
    console.error('[product purchase layout] Unable to read the STOQ selling plan.', error);
    return false;
  }
}

function isVisible(element) {
  let currentElement = element;

  while (currentElement) {
    if (currentElement.hidden || currentElement.getAttribute('aria-hidden') === 'true') return false;

    const styles = window.getComputedStyle(currentElement);
    if (styles.display === 'none' || styles.visibility === 'hidden') return false;

    currentElement = currentElement.parentElement;
  }

  return true;
}

function findNotifyMeButton(container) {
  const controls = container.querySelectorAll('button, a, [role="button"]');

  return [...controls].find(control => {
    const label = [control.textContent, control.getAttribute('aria-label'), control.getAttribute('title'), control.value]
      .filter(Boolean)
      .join(' ')
      .trim();
    return (control.dataset.themeNotifyButton === 'true'
      || /notify me when available/i.test(label)) && isVisible(control);
  });
}

function getPreorderLayoutState(buttonsContainer) {
  let state = preorderLayoutStates.get(buttonsContainer);

  if (!state) {
    state = { variantId: null, descriptionSnapshot: null, availabilityAnchor: null };
    preorderLayoutStates.set(buttonsContainer, state);
  }

  const variantId = buttonsContainer.closest('product-info')?.querySelector('.product-variant-id')?.value || '';
  if (state.variantId !== variantId) {
    state.variantId = variantId;
    state.descriptionSnapshot = null;
  }

  return state;
}

function ensureAvailabilityAnchor(availability, state) {
  if (!availability || state.availabilityAnchor?.parentNode) return;

  state.availabilityAnchor = document.createComment('product-availability-position');
  availability.parentNode?.insertBefore(state.availabilityAnchor, availability);
}

function restoreAvailabilityPosition(availability, state) {
  const anchor = state.availabilityAnchor;
  if (!availability || !anchor?.parentNode || anchor.nextSibling === availability) return;

  anchor.parentNode.insertBefore(availability, anchor.nextSibling);
}

function setPreorderButtonFallback(button, shouldShowPreorder, translatedLabel) {
  const label = button?.querySelector('span');
  if (!label) return;

  if (shouldShowPreorder) {
    if (!button.dataset.preorderFallbackOriginalLabel && !isPreorderButton(button)) {
      button.dataset.preorderFallbackOriginalLabel = label.textContent;
    }

    const preorderLabel = translatedLabel || 'Preorder';
    if (label.textContent !== preorderLabel) label.textContent = preorderLabel;
    button.dataset.themePreorderLabelActive = 'true';
    return;
  }

  if (button.dataset.preorderFallbackOriginalLabel) {
    label.textContent = button.dataset.preorderFallbackOriginalLabel;
    delete button.dataset.preorderFallbackOriginalLabel;
  }
  if (button) delete button.dataset.themePreorderLabelActive;
}

function setNotifyButtonLabel(button, text) {
  if (!button || !text) return;

  button.dataset.themeNotifyButton = 'true';
  const walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  let node;

  while ((node = walker.nextNode())) {
    if (node.textContent.trim() && !node.parentElement.closest('svg, [data-theme-notify-icon], .loading__spinner')) {
      textNodes.push(node);
    }
  }

  // Keep app-owned elements and click handlers when replacing the label.
  if (!textNodes.length) {
    button.append(document.createTextNode(text));
  } else {
    if (textNodes[0].textContent !== text) textNodes[0].textContent = text;
    textNodes.slice(1).forEach(textNode => { textNode.textContent = ''; });
  }
  if (button.getAttribute('aria-label') !== text) button.setAttribute('aria-label', text);
}

function restorePreorderDescription(purchaseControls, notifyContainer, buttonsContainer, state) {
  if (!purchaseControls || !state.descriptionSnapshot) return null;

  const restoredDescription = state.descriptionSnapshot.cloneNode(true);
  restoredDescription.dataset.themePreorderDescriptionFallback = 'true';

  const insertionReference = notifyContainer || buttonsContainer;
  insertionReference.parentElement?.insertBefore(restoredDescription, insertionReference);

  return restoredDescription;
}

function cacheRemovedPreorderDescription(mutations, buttonsContainer) {
  const state = getPreorderLayoutState(buttonsContainer);

  for (const mutation of mutations) {
    if (mutation.type !== 'childList') continue;

    for (const removedNode of mutation.removedNodes) {
      if (removedNode.nodeType !== 1) continue;

      const description = removedNode.matches('.restock-rocket-preorder-description')
        ? removedNode
        : removedNode.querySelector('.restock-rocket-preorder-description');

      if (!description || description.dataset.themePreorderDescriptionFallback || !description.textContent.trim()) {
        continue;
      }

      state.descriptionSnapshot = description.cloneNode(true);
      return;
    }
  }
}

function updatePreorderPurchaseLayout(buttonsContainer) {
  const state = getPreorderLayoutState(buttonsContainer);
  const submitButton = buttonsContainer.querySelector(':scope > .product-form__submit');
  const purchaseControls = buttonsContainer.closest('.product-purchase-controls');
  const notifyButton = findNotifyMeButton(purchaseControls || buttonsContainer);
  const notifyContainer = notifyButton?.closest('.restock-rocket-button-container') || notifyButton;
  const availability = purchaseControls?.querySelector('.product-availability');
  const hasNotifyButton = Boolean(notifyButton);
  const existingPreorderMessage = purchaseControls?.querySelector(
    '.restock-rocket-preorder-description:not([data-theme-preorder-description-fallback])'
  );
  const fallbackPreorderMessage = purchaseControls?.querySelector(
    '.restock-rocket-preorder-description[data-theme-preorder-description-fallback]'
  );
  const hasStoqPlan = hasStoqPreorderPlan(buttonsContainer);
  // A panel created for STOQ must not keep the next in-stock variant in preorder state.
  const hasServerPreorderPanel = Boolean(
    availability?.querySelector('[data-availability-panel="preorder"]:not([data-client-preorder-panel])')
  );
  const hasServerSoldOutPanel = Boolean(
    availability?.querySelector('[data-availability-panel="sold-out"]')
  );
  const isPreorderState = hasStoqPlan || Boolean(existingPreorderMessage) || hasServerPreorderPanel;
  const isSoldOutState = !isPreorderState && (
    hasServerSoldOutPanel
    || isSoldOutButton(submitButton)
    || (hasNotifyButton && Boolean(submitButton?.disabled))
  );
  const isNotifyState = hasNotifyButton && isSoldOutState && !isPreorderState;

  ensureAvailabilityAnchor(availability, state);
  restoreAvailabilityPosition(availability, state);

  if (fallbackPreorderMessage && (!state.descriptionSnapshot || existingPreorderMessage)) {
    fallbackPreorderMessage.remove();
  }

  let preorderMessage = isPreorderState
    ? existingPreorderMessage || fallbackPreorderMessage
    : null;

  if (!preorderMessage && isPreorderState) {
    preorderMessage = restorePreorderDescription(purchaseControls, notifyContainer, buttonsContainer, state);
  }

  if (preorderMessage) {
    preorderMessage.classList.add('product-purchase-controls__preorder-message');

    if (isPreorderState) {
      preorderMessage.classList.remove('hidden');
      preorderMessage.removeAttribute('hidden');
      preorderMessage.removeAttribute('aria-hidden');
    }

    if (!preorderMessage.dataset.themePreorderDescriptionFallback && preorderMessage.textContent.trim()) {
      state.descriptionSnapshot = preorderMessage.cloneNode(true);
    }
  }

  availability?.refreshPreorderDates?.();

  if (notifyContainer) {
    notifyContainer.classList.add('product-purchase-controls__notify');

    setNotifyButtonLabel(notifyButton, availability?.dataset.notifyButtonLabel || 'Notify me when available');
    delete notifyButton.dataset.notifyFallbackLabel;
  }

  if (preorderMessage && notifyContainer && !availability && preorderMessage.nextElementSibling !== notifyContainer) {
    preorderMessage.insertAdjacentElement('afterend', notifyContainer);
  }

  if (availability && notifyContainer?.parentElement) {
    const notifyIsInsideProductForm = Boolean(notifyContainer.closest('form'));

    if (notifyIsInsideProductForm) {
      restoreAvailabilityPosition(availability, state);
    } else if (isPreorderState && notifyContainer.parentElement === availability.parentElement) {
      if (notifyContainer.nextElementSibling !== availability) {
        notifyContainer.parentElement.insertBefore(notifyContainer, availability);
      }
    } else if (isSoldOutState && notifyContainer.nextElementSibling !== availability) {
      notifyContainer.parentElement.insertBefore(availability, notifyContainer.nextElementSibling);
    } else if (!isSoldOutState && notifyContainer.previousElementSibling !== availability) {
      notifyContainer.parentElement.insertBefore(availability, notifyContainer);
    }
  }

  setPreorderButtonFallback(submitButton, isPreorderState, availability?.dataset.preorderButtonLabel);
  buttonsContainer.classList.toggle(
    'product-form__buttons--preorder',
    isPreorderState
  );
  buttonsContainer.classList.toggle('product-form__buttons--notify', isNotifyState);
  buttonsContainer.classList.toggle('product-form__buttons--sold-out', isSoldOutState);
  purchaseControls?.classList.toggle('product-purchase-controls--preorder', isPreorderState);
  purchaseControls?.classList.toggle('product-purchase-controls--notify', isNotifyState);
  purchaseControls?.classList.toggle('product-purchase-controls--sold-out', isSoldOutState);
}

function watchPreorderPurchaseLayout(buttonsContainer) {
  updatePreorderPurchaseLayout(buttonsContainer);
  const purchaseControls = buttonsContainer.closest('.product-purchase-controls') || buttonsContainer;
  let updateScheduled = false;

  const observer = new MutationObserver((mutations) => {
    cacheRemovedPreorderDescription(mutations, buttonsContainer);

    if (updateScheduled) return;

    updateScheduled = true;
    window.requestAnimationFrame(() => {
      try {
        updatePreorderPurchaseLayout(buttonsContainer);
      } catch (error) {
        console.error('[product purchase layout] Failed to update preorder layout.', error);
      } finally {
        updateScheduled = false;
      }
    });
  });

  observer.observe(purchaseControls, {
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
    childList: true,
    characterData: true,
    subtree: true,
  });
}

document.querySelectorAll('.product-purchase-controls .product-form__buttons').forEach(buttonsContainer => {
  try {
    watchPreorderPurchaseLayout(buttonsContainer);
  } catch (error) {
    console.error('[product purchase layout] Failed to initialise preorder layout.', error);
  }
});
